import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ClaimResponse as Claim } from '@sentara-health/sentara-api-hooks-core';
import {
  Button,
  Paragraph,
  SearchIcon,
  CloseIcon,
  InputField,
  CardView,
} from '@sentara-health/optimahealth_web';
import { useIndexedDBSearch } from './useIndexedDBSearch';
import { getUniqueParts, formatMedicalText } from 'common';

export interface UseTypeAheadSearchMedicalClaimsProps {
  originalClaimsData: React.MutableRefObject<Claim[]>;
  setMedicalClaims: (claims: Claim[]) => void;
  onSmartSearchSelect?: (claim: Claim) => void;
  onSearchSubmitted?: () => void;
  isMobile?: boolean;
}

export interface UseTypeAheadSearchMedicalClaimsReturn {
  renderTypeAheadSearch: () => JSX.Element;
  searchNoRecords: boolean;
  searchInputValue: string;
  handleSelectSearchResult: (claim: Claim) => void;
  handleSearchInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchButtonClick: (
    e: React.MouseEvent<HTMLButtonElement>
  ) => Promise<void>;
  clearTypeAheadSearch: () => void;
  performSearchAndApply: (searchText: string) => Promise<void>;
}

export const useTypeAheadSearchMedicalClaims = ({
  originalClaimsData,
  setMedicalClaims,
  onSmartSearchSelect,
  onSearchSubmitted,
  isMobile = false,
}: UseTypeAheadSearchMedicalClaimsProps): UseTypeAheadSearchMedicalClaimsReturn => {
  const { t } = useTranslation();
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const {
    changeSearchInput,
    filteredData,
    getTotalAmount,
    isListShow,
    searchInputValue,
    searchNoRecords,
    setSearchNoRecords,
    setSearchInputValue,
    setFilteredData,
    setIsListShow,
    typeAheadSearch,
  } = useIndexedDBSearch();

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setIsListShow(false);
      }
    };

    if (isListShow) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isListShow, setIsListShow]);

  // Properly set the medical claims with the selected item
  const handleSelectSearchResult = (selectedItem: Claim) => {
    if (!selectedItem) return;
    setMedicalClaims([selectedItem]);
    setFilteredData([]);
    setIsListShow(false);
    if (typeof onSmartSearchSelect === 'function') {
      onSmartSearchSelect(selectedItem);
    }
  };

  // Handle input change for search field
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length === 0) {
      setFilteredData([]);
      setIsListShow(false);
      setMedicalClaims(originalClaimsData.current);
      setSearchInputValue('');
    } else {
      changeSearchInput(value);
    }
  };

  // Handle Enter and Escape key press on search input
  const handleSearchInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchButtonClick(e as any);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsListShow(false);
    }
  };

  const handleSearchButtonClick = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    await typeAheadSearch(searchInputValue);
    if (searchNoRecords) {
      setMedicalClaims([]);
    } else if (filteredData.length === 0) {
      setMedicalClaims(originalClaimsData.current);
      setSearchInputValue('');
    } else {
      setMedicalClaims(filteredData);
    }

    setFilteredData([]);
    setIsListShow(false);
    
    if (typeof onSmartSearchSelect === 'function') {
      onSmartSearchSelect({} as Claim);
    }
    onSearchSubmitted?.();
  };

  const clearTypeAheadSearch = () => {
    setFilteredData([]);
    setIsListShow(false);
    setMedicalClaims(originalClaimsData.current);
    setSearchInputValue('');
    setSearchNoRecords(false);
  };

  const performSearchAndApply = async (searchText: string) => {
    setSearchInputValue(searchText);
    const results = await typeAheadSearch(searchText);
    if (results.length === 0) {
      setMedicalClaims([]);
    } else {
      setMedicalClaims(results);
    }
    setFilteredData([]);
    setIsListShow(false);
    
    if (typeof onSmartSearchSelect === 'function') {
      onSmartSearchSelect({} as Claim);
    }
  };

  const renderTypeAheadSearch = (): JSX.Element => {
    return (
      <>
        {/* TypeAhead Search */}
        <CardView variant="lightCream" radius="md" className="p-4 w-100">
          <Paragraph
            level={3}
            variant="medium"
            text={t('medicalClaims.smartSearchTitle')}
          />
          <div className="d-flex align-items-center gap-3">
            <div className="flex-1 position-relative" ref={searchContainerRef}>
              <div className="position-relative">
                {searchInputValue.length === 0 && (
                  <span className="position-absolute seob-search-icon">
                    <SearchIcon />
                  </span>
                )}
                <InputField
                  className={`p-3 ${
                    searchInputValue.length === 0
                      ? 'seob-search-input-placeholder'
                      : 'seob-input-placeholder pe-5'
                  }`}
                  name="Begin typing..."
                  placeholder={t('medicalClaims.beginTyping')}
                  type="text"
                  value={searchInputValue}
                  onChange={handleSearchInputChange}
                  onKeyDown={handleSearchInputKeyDown}
                  maxLength={100}
                />
                {searchInputValue.length > 0 && (
                  <span className="position-absolute seob-search-close-icon">
                    <Button
                      className="p-0 bg-transparent border-0 mw-auto seob-search-close-btn"
                      onClick={clearTypeAheadSearch}
                    >
                      <CloseIcon />
                    </Button>
                  </span>
                )}
              </div>

              {isListShow && filteredData?.length > 0 && (
                <div className="rounded bg-white position-absolute overflow-auto shadow type-ahead-div">
                  <Paragraph
                    level={2}
                    variant="regular"
                    text={`${filteredData?.length} ${t(
                      'medicalClaims.suggestionsFound'
                    )}`}
                    className="p-3 pb-0"
                    textColor="disable"
                  />
                  <hr className="m-0 mb-3" />
                  <ul className="m-0 list-unstyled">
                    {filteredData?.map((item) => {
                      const itemKey = `${item?.id || ''}-${
                        item?.normalizedStatus || ''
                      }-${item?.payment?.date || ''}`;
                      return (
                        <li key={itemKey} className="search-item px-3">
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              handleSelectSearchResult(item);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleSelectSearchResult(item);
                              }
                            }}
                            className="cursor-pointer"
                            aria-label={`Select ${
                              (item?.type?.coding as any)?.display ?? 'claim'
                            }`}
                          >
                            <Paragraph
                              variant="medium"
                              textColor="primary"
                              className="m-0 p-0"
                              text={` ${
                                getUniqueParts(
                                  (item?.type?.coding as any)?.display
                                )?.join(' | ') ?? ''
                              }`}
                            />
                            <Paragraph
                              level={2}
                              variant="regular"
                              textColor="disable"
                              text={` ${
                                (item?.normalizedStatus as string) ?? ''
                              } | ${formatMedicalText(item?.extension?.[4]?.value ?? '')} | ${
                                formatMedicalText(item?.extension?.[1]?.value ?? '')
                              } | ${item?.normalizedServiceDate ?? ''} | ${
                                item?.total
                                  ? `$${getTotalAmount(item.total)}`
                                  : ''
                              } `}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
            {!isMobile && (
              <Button
                variant={'primary_btn'}
                iconLeft={<SearchIcon fill="#fff" />}
                onClick={handleSearchButtonClick}
              >
                {`${t('common.search')}`}
              </Button>
            )}
          </div>

          <Paragraph
            level={2}
            className="m-1"
            textColor="disable"
            text={`${t('medicalClaims.smartSearchPlaceholder')}`}
          />
        </CardView>

        {/* End of TypeAhead Search */}
      </>
    );
  };

  return {
    handleSelectSearchResult,
    handleSearchInputChange,
    handleSearchButtonClick,
    renderTypeAheadSearch,
    searchNoRecords,
    searchInputValue,
    clearTypeAheadSearch,
    performSearchAndApply,
  };
};
